import ExternalLink from './ExternalLink.jsx';

// The portal builds the link from validated GUIDs on one fixed host; the page checks the exact shapes again,
// so anything else (another host, a query string, an extra segment) renders no link.
const GUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const BLADES = [
  `Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/~/(?:Credentials|Overview)/appId/${GUID}`,
  `Microsoft_AAD_IAM/ManagedAppMenuBlade/~/Overview/objectId/${GUID}/appId/${GUID}`,
  `Microsoft_AAD_UsersAndTenants/UserProfileMenuBlade/~/overview/userId/${GUID}`,
  `Microsoft_AAD_IAM/GroupDetailsMenuBlade/~/Overview/groupId/${GUID}`,
  'Microsoft_AAD_IAM/AuditLogsBlade',
  `Microsoft_AAD_UsersAndTenants/UserProfileMenuBlade/~/SignIns/userId/${GUID}`,
  'Microsoft_AAD_IAM/IdentityProtectionMenuBlade/~/RiskDetections',
];
export const ENTRA_LINK = new RegExp(`^https://entra\\.microsoft\\.com/#view/(?:${BLADES.join('|')})$`);

const text = (v) => (typeof v === 'string' && v.trim() !== '' ? v : null);

/**
 * The story of a notice: what happened, why it matters, why this severity and what to do, for a guardian who has no
 * Entra admin access. The portal writes and cleans every string; here they are shown as text.
 */
function WatchStory({ story }) {
  const actions = Array.isArray(story?.actions) ? story.actions.filter((a) => text(a)) : [];
  const parts = [text(story?.story), text(story?.why), text(story?.severity), text(story?.noAction)];
  if (actions.length === 0 && parts.every((p) => p === null)) return null;
  return (
    <section className="space-y-2" aria-labelledby="watch-story-heading">
      <h3 id="watch-story-heading" className="sr-only">What happened</h3>
      {parts[0] ? <p className="text-sm text-slate-800">{parts[0]}</p> : null}
      {parts[1] ? <p className="text-sm text-slate-700"><span className="font-semibold">Why it matters:</span> {parts[1]}</p> : null}
      {parts[2] ? <p className="text-sm text-slate-600"><span className="font-semibold">Severity:</span> {parts[2]}</p> : null}
      {actions.length > 0 ? (
        <div className="space-y-1">
          <h3 className="text-sm font-semibold text-slate-900">What to do</h3>
          <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-700">
            {actions.map((a) => <li key={a}>{a}</li>)}
          </ol>
        </div>
      ) : null}
      {parts[3] ? <p className="text-sm text-slate-700">{parts[3]}</p> : null}
    </section>
  );
}

/**
 * The story and the "Event details" of a Watch notice (ENTRAID-344): the story first (what happened, why it matters, what
 * to do), then what the Microsoft Entra audit log says about the change, one optional link into Entra and, for a notice
 * with no story, what it usually means. The portal decides the rows (per audience) and cleans the values; every value
 * here is shown as text.
 */
export default function WatchEventDetails({ details }) {
  const rows = Array.isArray(details?.rows) ? details.rows : [];
  const story = details?.story ?? null;
  if (rows.length === 0 && !story) return null;
  const url = typeof details.link?.url === 'string' && ENTRA_LINK.test(details.link.url) ? details.link.url : null;
  return (
    <div className="space-y-4">
      <WatchStory story={story} />
      {rows.length > 0 ? (
        <section className="space-y-2" aria-labelledby="watch-event-details-heading">
          <h3 id="watch-event-details-heading" className="text-sm font-semibold text-slate-900">Event details</h3>
          <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-[12rem_1fr]">
            {rows.map((r) => (
              <div key={`${r.label}|${r.value}`} className="contents">
                <dt className="text-slate-500">{r.label}</dt>
                <dd className="break-words">{r.value}</dd>
              </div>
            ))}
          </dl>
          {url ? <p className="text-sm"><ExternalLink href={url} className="font-medium text-ipc-light hover:underline">{details.link.label || 'Open in Microsoft Entra'}</ExternalLink></p> : null}
          {details.meaning && !story ? <p className="text-sm text-slate-700"><span className="font-semibold">What this usually means:</span> {details.meaning}</p> : null}
        </section>
      ) : null}
    </div>
  );
}
